import { useState, useMemo, useCallback } from "react";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import {
	Box,
	Typography,
	Paper,
	IconButton,
	Tooltip,
	CircularProgress,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Divider,
} from "@mui/material";
import {
	ChevronLeft,
	ChevronRight,
	Today,
	Description,
	AccessTime,
	Person,
	School,
	Email,
	LocalShipping,
} from "@mui/icons-material";
import isAuthenticated from "../../components/Utils/isAuthenticated";
import { getScanningCalendarData } from "../../components/Api/Getters";

// Helper to generate calendar grid
function generateCalendarDays(year, month) {
	const firstDay = dayjs(new Date(year, month, 1));
	const lastDay = firstDay.endOf("month");
	const startOfWeek = firstDay.startOf("week");
	const endOfWeek = lastDay.endOf("week");

	const days = [];
	let current = startOfWeek;

	while (current.isBefore(endOfWeek) || current.isSame(endOfWeek, "day")) {
		days.push(current);
		current = current.add(1, "day");
	}

	return days;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function ScanningCalendar() {
	const auth = isAuthenticated();
	const token = auth.token;

	// Current month being viewed
	const [viewDate, setViewDate] = useState(dayjs());
	const [selectedAssign2, setSelectedAssign2] = useState(null);

	const year = viewDate.year();
	const month = viewDate.month();

	// Calculate the date range for the API call (entire month + buffer for calendar grid)
	const startDate = dayjs(new Date(year, month, 1))
		.startOf("week")
		.format("YYYY-MM-DD");
	const endDate = dayjs(new Date(year, month, 1))
		.endOf("month")
		.endOf("week")
		.format("YYYY-MM-DD");

	// Fetch calendar data
	const { data: calendarData, isLoading } = useQuery(
		["scanningCalendar", startDate, endDate],
		() => getScanningCalendarData(token, startDate, endDate),
		{
			enabled: !!token,
			staleTime: 1000 * 60 * 5, // 5 minutes
			refetchOnWindowFocus: false,
		},
	);

	// Generate calendar days
	const calendarDays = useMemo(() => generateCalendarDays(year, month), [year, month]);

	// Process assign2 requests into a map by date
	const assign2ByDate = useMemo(() => {
		if (!calendarData?.assign2_requests) return {};

		const map = {};
		calendarData.assign2_requests.forEach((req) => {
			if (req.received) {
				const dateKey = dayjs(req.received).format("YYYY-MM-DD");
				if (!map[dateKey]) map[dateKey] = [];
				map[dateKey].push(req);
			}
		});

		// Sort each day's requests by time
		Object.keys(map).forEach((key) => {
			map[key].sort((a, b) => dayjs(a.received).valueOf() - dayjs(b.received).valueOf());
		});

		return map;
	}, [calendarData]);

	// Navigation handlers
	const goToPrevMonth = useCallback(() => {
		setViewDate((prev) => prev.subtract(1, "month"));
	}, []);

	const goToNextMonth = useCallback(() => {
		setViewDate((prev) => prev.add(1, "month"));
	}, []);

	const goToToday = useCallback(() => {
		setViewDate(dayjs());
	}, []);

	console.log(assign2ByDate);

	// Render a single calendar day cell
	const renderDayCell = (day) => {
		const dateKey = day.format("YYYY-MM-DD");
		const isCurrentMonth = day.month() === month;
		const isToday = day.isSame(dayjs(), "day");
		const mcCount = calendarData?.mc_counts?.[dateKey] || 0;
		const assign2List = assign2ByDate[dateKey] || [];

		return (
			<Box
				key={dateKey}
				sx={{
					border: "1px solid #e0e0e0",
					minHeight: 120,
					backgroundColor: isCurrentMonth ? (isToday ? "#e8f5e9" : "#fff") : "#f5f5f5",
					opacity: isCurrentMonth ? 1 : 0.6,
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
				}}
			>
				{/* Day header */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						px: 1,
						py: 0.5,
						borderBottom: "1px solid #e0e0e0",
						backgroundColor: isToday ? "#275d38" : "transparent",
					}}
				>
					<Typography
						variant="body2"
						sx={{
							fontWeight: isToday ? 700 : 500,
							color: isToday ? "#fff" : isCurrentMonth ? "#333" : "#999",
						}}
					>
						{day.format("D")}
					</Typography>
					{mcCount > 0 && (
						<Tooltip title={`${mcCount} Multiple Choice exam${mcCount > 1 ? "s" : ""} submitted`}>
							<Chip
								label={mcCount}
								size="small"
								sx={{
									height: 20,
									fontSize: "0.7rem",
									backgroundColor: "#1976d2",
									color: "#fff",
									"& .MuiChip-label": { px: 1 },
								}}
							/>
						</Tooltip>
					)}
				</Box>

				{/* Assign2 requests */}
				<Box sx={{ flex: 1, overflow: "auto", px: 0.5, py: 0.5 }}>
					{assign2List.map((req) => (
						<Tooltip
							key={req.reqnum}
							title={`${req.coursetitle} - ${req.instructor_name} @ ${dayjs(req.received).format("h:mm A")}`}
						>
							<Box
								onClick={() => setSelectedAssign2(req)}
								sx={{
									backgroundColor: "#ff9800",
									color: "#fff",
									borderRadius: 1,
									px: 0.75,
									py: 0.25,
									mb: 0.5,
									fontSize: "0.7rem",
									cursor: "pointer",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									"&:hover": {
										backgroundColor: "#f57c00",
									},
								}}
							>
								<Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
									{dayjs(req.received).format("h:mm A")}
								</Box>
								{req.coursetitle}
							</Box>
						</Tooltip>
					))}
				</Box>
			</Box>
		);
	};

	return (
		<div className="bg-digitalWhite min-h-[78svh] flex flex-col">
			<div className="w-full max-w-7xl mx-auto px-4 py-6">
				{/* Header */}
				<Paper elevation={0} sx={{ mb: 3, p: 2, backgroundColor: "#fff" }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							flexWrap: "wrap",
							gap: 2,
						}}
					>
						<Box>
							<Typography variant="h5" sx={{ color: "#275d38", fontWeight: 600 }}>
								Scanning Calendar
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Track exam scanning requests and drop-offs
							</Typography>
						</Box>

						{/* Navigation */}
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Tooltip title="Previous Month">
								<IconButton onClick={goToPrevMonth} size="small">
									<ChevronLeft />
								</IconButton>
							</Tooltip>

							<Typography variant="h6" sx={{ minWidth: 180, textAlign: "center", fontWeight: 500 }}>
								{viewDate.format("MMMM YYYY")}
							</Typography>

							<Tooltip title="Next Month">
								<IconButton onClick={goToNextMonth} size="small">
									<ChevronRight />
								</IconButton>
							</Tooltip>

							<Tooltip title="Go to Today">
								<IconButton onClick={goToToday} size="small" color="primary">
									<Today />
								</IconButton>
							</Tooltip>
						</Box>
					</Box>

					{/* Legend */}
					<Box sx={{ display: "flex", gap: 3, mt: 2 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Box
								sx={{
									width: 16,
									height: 16,
									borderRadius: 0.5,
									backgroundColor: "#1976d2",
								}}
							/>
							<Typography variant="body2">Multiple Choice Submissions (daily count)</Typography>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Box
								sx={{
									width: 16,
									height: 16,
									borderRadius: 0.5,
									backgroundColor: "#ff9800",
								}}
							/>
							<Typography variant="body2">Assign2 Written Exam Drop-offs</Typography>
						</Box>
					</Box>
				</Paper>

				{/* Calendar Grid */}
				{isLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: 400,
						}}
					>
						<CircularProgress color="primary" />
					</Box>
				) : (
					<Paper elevation={0} sx={{ backgroundColor: "#fff", overflow: "hidden" }}>
						{/* Weekday headers */}
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(7, 1fr)",
								backgroundColor: "#275d38",
							}}
						>
							{WEEKDAYS.map((day) => (
								<Box
									key={day}
									sx={{
										py: 1,
										textAlign: "center",
									}}
								>
									<Typography variant="body2" sx={{ fontWeight: 600, color: "#fff" }}>
										{day}
									</Typography>
								</Box>
							))}
						</Box>

						{/* Calendar days grid */}
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(7, 1fr)",
							}}
						>
							{calendarDays.map(renderDayCell)}
						</Box>
					</Paper>
				)}
			</div>

			{/* Assign2 Detail Dialog */}
			<Dialog
				open={!!selectedAssign2}
				onClose={() => setSelectedAssign2(null)}
				maxWidth="md"
				fullWidth
			>
				{selectedAssign2 && (
					<>
						<DialogTitle
							sx={{
								backgroundColor: "#ff9800",
								color: "#fff",
								display: "flex",
								alignItems: "center",
								gap: 1,
							}}
						>
							<Description />
							Assign2 Written Exam Details
						</DialogTitle>
						<DialogContent sx={{ mt: 2 }}>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
								<Box>
									<Typography variant="overline" color="text.secondary">
										Request Number
									</Typography>
									<Typography variant="h6">{selectedAssign2.reqnum}</Typography>
								</Box>

								<Divider />

								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<School color="primary" />
									<Box>
										<Typography variant="overline" color="text.secondary">
											Course
										</Typography>
										<Typography>{selectedAssign2.coursetitle}</Typography>
									</Box>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<Person color="primary" />
									<Box>
										<Typography variant="overline" color="text.secondary">
											Instructor
										</Typography>
										<Typography>{selectedAssign2.instructor_name}</Typography>
									</Box>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<AccessTime color="primary" />
									<Box>
										<Typography variant="overline" color="text.secondary">
											Drop-off Date/Time
										</Typography>
										<Typography>
											{dayjs(selectedAssign2.received).format("dddd, MMMM D, YYYY [at] h:mm A")}
										</Typography>
									</Box>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<Email color="primary" />
									<Box>
										<Typography variant="overline" color="text.secondary">
											Email
										</Typography>
										<Typography>{selectedAssign2.email1 || "N/A"}</Typography>
									</Box>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<LocalShipping color="primary" />
									<Box>
										<Typography variant="overline" color="text.secondary">
											Delivery Method
										</Typography>
										<Typography>{selectedAssign2.delivery || "N/A"}</Typography>
									</Box>
								</Box>

								<Divider />

								<Box sx={{ display: "flex", gap: 3 }}>
									<Box>
										<Typography variant="overline" color="text.secondary">
											Booklets
										</Typography>
										<Typography>{selectedAssign2.booklets || "N/A"}</Typography>
									</Box>
									<Box>
										<Typography variant="overline" color="text.secondary">
											Sheets/Pages
										</Typography>
										<Typography>{selectedAssign2.sheets || "N/A"}</Typography>
									</Box>
									<Box>
										<Typography variant="overline" color="text.secondary">
											Sided
										</Typography>
										<Typography>{selectedAssign2.sided || "N/A"}</Typography>
									</Box>
								</Box>

								<Box>
									<Typography variant="overline" color="text.secondary">
										Status
									</Typography>
									<Chip
										label={selectedAssign2.status || "Unknown"}
										size="small"
										color={
											selectedAssign2.status === "completed"
												? "success"
												: selectedAssign2.status === "pending"
													? "warning"
													: "default"
										}
									/>
								</Box>
							</Box>
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setSelectedAssign2(null)}>Close</Button>
						</DialogActions>
					</>
				)}
			</Dialog>
		</div>
	);
}
